import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, SnackbarStatus, Typography, useSnackbar } from '@weareopenr/component-library';

import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { OptionGroupType } from 'src/constants';
import { createGlobalItem } from '../../api/createGlobalItem';
import { createOptionGroup, CreateOptionGroupRequest } from '../../api/createOptionGroup';
import { addItemsToMenu } from '../../api/addItemsToMenu';
import { FormVariant } from '../../types/menuItemForm.types';
import { formatGlobalItemPayload } from './utilities';
import { ICreateGlobalItemWizardProps, IGlobalItem, Steps } from './types';
import { GlobalItemTypeForm } from './GlobalItemTypeForm/GlobalItemTypeForm';
import { GlobalItemDetailsForm } from './GlobalItemDetailsForm/GlobalItemDetailsForm';
import { GlobalItemTaxRateForm } from './GlobalItemTaxRateForm/GlobalItemTaxRateForm';
import { GlobalItemBundleCategory } from './GlobalItemBundleCategory/GlobalItemBundleCategory';
import { GlobalItemPriceSchemes } from './GlobalItemPriceSchemes/GlobalItemPriceSchemes';
import { GlobalItemBundlePricing } from './GlobalItemBundlePricing/GlobalItemBundlePricing';

import chefPlating from 'src/assets/images/chef-plating.png';
import styles from './CreateGlobalItemWizard.module.scss';

interface IGlobalItemWizardProps extends Omit<ICreateGlobalItemWizardProps, 'open'> {
  step: Steps;
  setStep: (step: Steps) => void;
}

const GlobalItemWizard = ({ closeModal, menuId, sectionId, step, setStep }: IGlobalItemWizardProps) => {
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { trackEvent, captureException } = useUserTracking();
  const [globalItem, setGlobalItem] = useState<Partial<IGlobalItem>>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const variant = FormVariant.CREATE;
  const submitStep = globalItem.type === 'BUNDLE' ? Steps.bundlePricing : Steps.tax;
  const totalSteps = submitStep + 1;

  const updateGlobalItem = (data: Partial<IGlobalItem>) => {
    setGlobalItem((prev) => ({ ...prev, ...data }));
  };

  const createItem = async (data: Partial<IGlobalItem>) => {
    setIsSubmitting(true);
    try {
      const payload = formatGlobalItemPayload(data as IGlobalItem);
      const newItemId = (await createGlobalItem(payload)).item_id;
      const additionalItemRequests = [];

      if (data.type === 'BUNDLE') {
        const bundleGroup = data.modifier_groups[0];
        const reqBody: CreateOptionGroupRequest = {
          type: OptionGroupType.BUNDLE,
          name: bundleGroup.name,
          description: bundleGroup.description,
          max_choices: bundleGroup.max_choices,
          min_choices: bundleGroup.min_choices,
          options: bundleGroup.modifiers.map((mod) => ({
            item_id: mod.optionItemId,
            plu: mod.inheritPosId ? null : mod.posId || null,
            value_with_tax: mod.useDefaultPrice ? null : mod.valueWithTax || 0,
          })),
          item_ids: [newItemId],
          price_scheme_ids: data.price_scheme_ids,
        };

        additionalItemRequests.push(createOptionGroup(reqBody));
      }
      await Promise.all(additionalItemRequests);

      if (menuId) {
        await addItemsToMenu(menuId, [
          {
            item_id: newItemId,
            section_id: sectionId ?? null,
          },
        ]);
      }
      trackEvent('product_created', { productId: newItemId, productName: data.name, productType: data.type });
      showSnackbar({
        status: SnackbarStatus.SUCCESS,
        message: `You have successfully created ${data.name}.`,
      });
      navigate(`/items/${newItemId}`);
    } catch (e) {
      captureException(e, { tags: { action: 'create_product', productName: data.name, productType: data.type } });
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: e instanceof Error ? e.message : 'Something went wrong whilst creating a product',
      });
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Typography className={styles['wizard-steps']}>{`Step ${step + 1} of ${totalSteps}`}</Typography>

      {step === Steps.type && (
        <GlobalItemTypeForm
          variant={variant}
          setStep={setStep}
          setGlobalItem={updateGlobalItem}
          onCancel={closeModal}
          defaultType={globalItem.type}
        />
      )}

      {step === Steps.details && (
        <GlobalItemDetailsForm
          variant={variant}
          item={globalItem}
          onCloseOrBack={setStep}
          setGlobalItem={updateGlobalItem}
          showImageField
        />
      )}
      {step === Steps.tax && (
        <GlobalItemTaxRateForm
          variant={variant}
          item={globalItem}
          isSubmitting={isSubmitting}
          onSubmitItem={submitStep === Steps.tax && createItem}
          onCloseOrBack={setStep}
          setGlobalItem={updateGlobalItem}
        />
      )}
      {step === Steps.bundleCategory && (
        <GlobalItemBundleCategory
          wizardItem={globalItem}
          onBackOrClose={() => setStep(Steps.tax)}
          onContinue={() => setStep(Steps.bundlePriceSchemes)}
          setGlobalItem={updateGlobalItem}
        />
      )}
      {step === Steps.bundlePriceSchemes && (
        <GlobalItemPriceSchemes
          wizardItem={globalItem}
          onBackOrClose={() => setStep(Steps.bundleCategory)}
          onContinue={() => setStep(Steps.bundlePricing)}
          setGlobalItem={updateGlobalItem}
        />
      )}
      {step === Steps.bundlePricing && (
        <GlobalItemBundlePricing
          wizardOnSubmit={createItem}
          wizardItem={globalItem}
          goBack={() => setStep(Steps.bundlePriceSchemes)}
          isSubmitting={isSubmitting}
        />
      )}
    </>
  );
};

export const CreateGlobalItemWizard = ({ open, closeModal, menuId, sectionId }: ICreateGlobalItemWizardProps) => {
  const [step, setStep] = useState<Steps>(Steps.type);

  const handleClose = () => {
    setStep(Steps.type);
    closeModal();
  };

  return (
    <Modal
      open={open}
      closeModal={handleClose}
      image={step === Steps.bundleCategory ? undefined : chefPlating}
      imageAlt="chef"
    >
      <GlobalItemWizard closeModal={handleClose} menuId={menuId} sectionId={sectionId} step={step} setStep={setStep} />
    </Modal>
  );
};
